/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 * Handles session persistence and token refresh automatically via Amplify.
 * Listens for auth events to handle session expiration gracefully.
 *
 * Requirements: 2.3, 2.6, 7.1, 7.2, 7.3, 7.4
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';
import { ensureAmplifyConfigured, hasStoredCognitoSession } from '../lib/amplify';
import { identifyUser, resetPostHog } from '../lib/posthog';

// Amplify (auth SDK, Hub, profile API) is imported dynamically below so the
// logged-out landing page renders without pulling in the heavy aws-amplify
// bundle. The SDK only loads once there's a session to verify or an auth action.

interface User {
  userId: string;
  email: string;
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  sessionExpired: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      await ensureAmplifyConfigured();
      const { getAuthenticatedUser } = await import('../lib/auth');
      const { getMyProfile } = await import('../lib/profile');
      const authenticatedUser = await getAuthenticatedUser();
      setUser(authenticatedUser);
      setSessionExpired(false);
      if (authenticatedUser) {
        identifyUser(authenticatedUser.userId, authenticatedUser.email);
      }

      // Fetch profile to get isAdmin status (Requirement 7.2)
      const profileResult = await getMyProfile();
      if (profileResult.success && profileResult.profile) {
        setIsAdmin(profileResult.profile.isAdmin);
      } else {
        setIsAdmin(false);
      }
    } catch {
      setUser(null);
      setIsAdmin(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await ensureAmplifyConfigured();
      const { signOutUser } = await import('../lib/auth');
      await signOutUser();
      resetPostHog();
      setUser(null);
      setIsAdmin(false); // Clear admin status on sign out (Requirement 7.3)
      setSessionExpired(false);
    } catch (error) {
      console.error('Sign out failed:', error);
      throw error;
    }
  }, []);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  // Tag Sentry events with the current user (id only — no PII beyond that) so
  // errors can be traced to a session. Cleared on sign-out / session loss.
  useEffect(() => {
    Sentry.setUser(user ? { id: user.userId } : null);
  }, [user]);

  // Check authentication status on mount.
  // Optimistic session gate: when there's no stored Cognito token we know the
  // visitor is logged out, so we render immediately WITHOUT loading Amplify
  // (the landing page never needs it). Only when a session might exist do we
  // load the SDK and verify properly.
  useEffect(() => {
    const checkAuth = async () => {
      if (!hasStoredCognitoSession()) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        await ensureAmplifyConfigured();
        const { isAuthenticated: checkIsAuthenticated } = await import('../lib/auth');
        const authenticated = await checkIsAuthenticated();
        if (authenticated) {
          await refreshUser();
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [refreshUser]);

  // Listen for auth events (token refresh, sign out, session expiration). Only
  // active while signed in — by then Amplify is loaded — so a logged-out
  // visitor never loads the SDK just to register this listener. Sign-in itself
  // is handled by SignInPage calling refreshUser(), not by this listener.
  const isAuthed = !!user;
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    let unlisten: (() => void) | undefined;

    (async () => {
      await ensureAmplifyConfigured();
      const { Hub } = await import('aws-amplify/utils');
      if (cancelled) return;
      unlisten = Hub.listen('auth', async ({ payload }) => {
        switch (payload.event) {
          case 'signedIn':
            await refreshUser();
            break;
          case 'signedOut':
            setUser(null);
            setIsAdmin(false);
            setSessionExpired(false);
            break;
          case 'tokenRefresh':
            console.log('Token refreshed');
            break;
          case 'tokenRefresh_failure':
            console.log('Token refresh failed - session expired');
            setUser(null);
            setIsAdmin(false);
            setSessionExpired(true);
            break;
          default:
            break;
        }
      });
    })();

    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [isAuthed, refreshUser]);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin,
    sessionExpired,
    signOut,
    refreshUser,
    clearSessionExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
